import { ChangeEvent, useRef } from 'react';

import { CloseIcon } from '@chakra-ui/icons';
import {
  Button,
  Container,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import MagnifyingGlassIcon from '~/assets/icons/magnifyingGlass.svg?react';
import { useScreenDetector } from '~/hooks/useScreenDetector';
import {
  useLazySearchPlaylistsQuery,
  useLazySearchSongsQuery,
} from '~/services/api/search.service';
import { useSetSelectedPlaylistIdValue } from '~/store/slices/playlist-hook';
import {
  useResultCountSelector,
  useSearchSelector,
  useSetSearchValue,
} from '~/store/slices/search-hook';
import { BRAND_BLACK_COLOR } from '~/themes/theme';

function SearchInput() {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const { isDesktop } = useScreenDetector();
  const value = useSearchSelector();
  const setSearchValue = useSetSearchValue();
  const [searchSongs] = useLazySearchSongsQuery();
  const [searchPlaylist] = useLazySearchPlaylistsQuery();
  const resultCount = useResultCountSelector();
  const setSelectedPlaylistIdValue = useSetSelectedPlaylistIdValue();

  const getCountryByLanguage = () => {
    switch (language) {
      case 'fr':
        return 'FRA';
      case 'es':
        return 'ESP';
      case 'en':
        return 'ENG';
      default:
        return 'FRA';
    }
  };

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value && event.target.value.length < 3) {
      return;
    }

    setSelectedPlaylistIdValue(null);
    setSearchValue(event.target.value);

    await searchSongs({
      search: event.target.value,
      userContext: 'BAM',
      country: getCountryByLanguage(),
    });

    await searchPlaylist({
      search: event.target.value,
      userContext: 'BAM',
      country: getCountryByLanguage(),
    });
  };

  const handleClear = () => {
    setSearchValue('');

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <Container
      maxW='3xl'
      minH={{ base: 100, lg: 180 }}
      display='flex'
      justifyContent='center'
      alignItems='center'
      py={4}
    >
      <Stack spacing={6} w='100%'>
        {isDesktop && (
          <Text fontSize='13px' textAlign='center'>
            {`${t('home')} > ${t('karaokeCatalogue')} ${value ? `> ${value}` : ''}`}
          </Text>
        )}

        <InputGroup>
          <InputLeftElement pointerEvents='none'>
            <MagnifyingGlassIcon />
          </InputLeftElement>
          <Input
            placeholder={t('searchPlaceholder')}
            ref={inputRef}
            onChange={debounce(handleChange, 1000)}
          />
          {value && (
            <InputRightElement width='4.5rem'>
              <Button h='1.75rem' size='sm' onClick={() => handleClear()}>
                <CloseIcon color={BRAND_BLACK_COLOR} />
              </Button>
            </InputRightElement>
          )}
        </InputGroup>

        {value && (
          <Stack spacing={0}>
            <Text fontSize='13px' textAlign='center'>
              {t('searchResults', { value })}
            </Text>
            <Text fontSize='13px' textAlign='center'>
              {t('numberOfResults', { count: resultCount })}
            </Text>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default SearchInput;
